// Rock golem character — chunky boulder body, smaller boulder head,
// stubby arms, crystal eyes. Toon-shaded so it matches the planet.
//
// Two color variants: "moss" (hider, mossy purple) and "ember" (seeker,
// warm orange crystal eyes). The seeker variant is hot-tinted so you can
// instantly tell which is which from a distance.

import * as THREE from 'three';
import { makeToonMaterial, addOutline } from '../render/flat.js';

function roughen(geo, amount = 0.15, seed = 1) {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const j = (Math.sin(i * 12.9 + seed) * 0.5 + 0.5) * amount * 2 - amount;
    pos.setXYZ(i, pos.getX(i) * (1 + j), pos.getY(i) * (1 + j), pos.getZ(i) * (1 + j));
  }
  geo.computeVertexNormals();
  return geo;
}

export function makeCharacter({
  bodyColor = 0x6a5f8a, mossColor = 0x4a3a7a, eyeColor = 0xffd285, isSeeker = false,
} = {}) {
  const grp = new THREE.Group();

  // Body — big roughed icosahedron, slightly squashed
  const bodyGeo = roughen(new THREE.IcosahedronGeometry(0.7, 1), 0.18, isSeeker ? 21 : 7);
  const body = new THREE.Mesh(bodyGeo, makeToonMaterial({ color: bodyColor, bands: 3, rim: 0.5 }));
  body.position.y = 0.85;
  body.scale.set(1.0, 0.95, 1.0);
  addOutline(body, 0.05);
  grp.add(body);

  // Mossy patches on the body
  const mossMat = makeToonMaterial({ color: mossColor, bands: 3, rim: 0.4 });
  for (let i = 0; i < 3; i++) {
    const m = new THREE.Mesh(
      roughen(new THREE.IcosahedronGeometry(0.22, 0), 0.2, i + 3),
      mossMat
    );
    addOutline(m, 0.025);
    const a = (i / 3) * Math.PI * 2 + 1.2;
    m.position.set(Math.cos(a) * 0.55, 0.95 + Math.sin(i) * 0.15, Math.sin(a) * 0.55);
    m.scale.set(1, 0.5, 1);
    grp.add(m);
  }

  // Head — smaller boulder, sits in a notch on the body
  const headGeo = roughen(new THREE.IcosahedronGeometry(0.4, 0), 0.16, isSeeker ? 41 : 11);
  const head = new THREE.Mesh(headGeo, makeToonMaterial({ color: bodyColor, bands: 3, rim: 0.5 }));
  head.position.y = 1.55;
  addOutline(head, 0.04);
  grp.add(head);

  // Crystal eyes — two glowing shards on the head
  const eyeMat = makeToonMaterial({
    color: eyeColor, bands: 2, rim: 0.8,
    emissive: eyeColor, emissiveIntensity: isSeeker ? 1.2 : 0.7,
  });
  const eyeL = new THREE.Mesh(new THREE.OctahedronGeometry(0.07, 0), eyeMat);
  const eyeR = new THREE.Mesh(new THREE.OctahedronGeometry(0.07, 0), eyeMat);
  eyeL.position.set(-0.13, 1.62, 0.34);
  eyeR.position.set( 0.13, 1.62, 0.34);
  grp.add(eyeL); grp.add(eyeR);

  // Stubby arms — capsules
  const armMat = makeToonMaterial({ color: bodyColor, bands: 3, rim: 0.5 });
  const makeArm = (side) => {
    const arm = new THREE.Mesh(
      roughen(new THREE.CapsuleGeometry(0.16, 0.35, 4, 6), 0.1, side + 5),
      armMat
    );
    addOutline(arm, 0.03);
    arm.position.set(side * 0.65, 0.95, 0);
    arm.rotation.z = side * 0.3;
    return arm;
  };
  const armL = makeArm(-1), armR = makeArm(1);
  grp.add(armL); grp.add(armR);

  // Stubby legs — short, thick
  const makeLeg = (side) => {
    const leg = new THREE.Mesh(
      roughen(new THREE.CapsuleGeometry(0.18, 0.25, 4, 6), 0.08, side + 9),
      armMat
    );
    addOutline(leg, 0.03);
    leg.position.set(side * 0.22, 0.32, 0);
    return leg;
  };
  const legL = makeLeg(-1), legR = makeLeg(1);
  grp.add(legL); grp.add(legR);

  grp.userData.bobTargets = { body, head, armL, armR, legL, legR };
  return grp;
}

// Walking bob/sway. Rocks lumber, so the bob is heavier and slower.
export function animateCharacter(charGroup, t, speed01) {
  const { body, head, armL, armR, legL, legR } = charGroup.userData.bobTargets;
  const phase = t * 5 * speed01;
  const bob = Math.sin(phase * 2) * 0.05 * speed01;
  body.position.y = 0.85 + bob;
  head.position.y = 1.55 + bob * 0.7;
  // Arm sway
  armL.rotation.x = Math.sin(phase) * 0.4 * speed01;
  armR.rotation.x = -Math.sin(phase) * 0.4 * speed01;
  // Leg sway (visible from third-person)
  legL.rotation.x = -Math.sin(phase) * 0.5 * speed01;
  legR.rotation.x = Math.sin(phase) * 0.5 * speed01;
}
